<template>
	<div class="content-page content-page--integration-manager">
		<CustomerNav />
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">{{ pageTitle }}</h2>
			</div>
			<div class="content-page__actions">
				<Button
					className="btn--has-icon"
					iconClassName="icon-more"
					labelText="Add"
					:btnClick="addIntegration"
					:iconBeforeLabel="true"
					:disabled="!canCreate"
				/>
			</div>
		</div>
		<div class="results-toolbar results-toolbar--search-simple-filter">
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					placeholder="Search by thirdparty or integration"
					v-model="filters.search"
				/>
			</div>
			<div class="results-toolbar__filter">
				<Dropdown
					:autosuggestion="true"
					:defaultValue="'Select Client'"
					:options="ddCustomerOptions()"
					v-model="filters.customerId"
				/>
				<DropdownV2
					:options="statusTypeOptions"
					v-model="filters.status"
					defaultValue="all"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="hasResults" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<IntegrationList
				:items="integrationsList"
				:thirdPartyId="thirdPartyId"
			/>
		</ScrollContainer>
		<Spinner v-if="loading" :processor="{ loading: true }" />
		<NoResults v-if="!loading && !hasResults" />

		<IntegrationFlyout
			:customerId="filters.customerId"
			:display="showModalIntegration"
			:integrations="integrations"
			:source="integration"
			:thirdparty="thirdParty"
		/>
	</div>
</template>
<script>
import Button from '@/components/forms/button/button';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Search from '@/components/forms/search/search';
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import IntegrationList from './integration-list';
import IntegrationFlyout from './integration-flyout';
import { mapActions, mapGetters } from 'vuex';
import ValidatePermissions from '@/sections/integration/validate-permission-mixins';
import CustomerOptionsMixins from '@/mixins/customers/customer-options';
import CustomerNav from '@/sections/shared/customer-navegation';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import { getDifferentValues } from '@/utils/function';

//constants
import {
	MODULE_NAME,
	INTEGRATION,
	INTEGRATIONS,
	RELOAD_INTEGRATIONS,
	SHOW_MODAL_INTEGRATION,
	SET_INTEGRATIONS
} from '@/sections/integration/integrations-mutation-types';
import { GET_CUSTOMER_OPTION_NAME } from '@/sections/options/options-mutation-type';
import _ from 'lodash';

export default {
	name: 'Integrations',
	inheritAttrs: false,
	mixins: [ValidatePermissions, CustomerOptionsMixins],
	components: {
		CustomerNav,
		Breadcrumbs,
		Button,
		Dropdown,
		IntegrationList,
		IntegrationFlyout,
		ResultCounter,
		Search,
		ScrollContainer,
		Spinner,
		NoResults,
		DropdownV2
	},
	props: {
		thirdPartyId: {
			type: Number
		},
		status: {
			type: String
		},
		customerId: {
			type: String
		},
		search: {
			type: String,
			default: ''
		},
		count: {
			type: Number
		},
		integrationId: {
			type: String
		}
	},
	data() {
		return {
			filters: {
				status: this.status,
				search: this.search,
				customerId: this.customerId
			},
			page: 1,
			countPerPage: this.count,
			defaultFilters: {
				search: '',
				status: 'all',
				customerId: null
			},
			loading: true,
			loadingMore: false,
			customerName: '',
			thirdParty: {},
			integrationIdData: 0,
			statusTypeOptions: [
				{
					text: 'All Status',
					value: 'all'
				},
				{
					text: 'Active',
					value: 'active'
				},
				{
					text: 'Inactive',
					value: 'inactive'
				}
			]
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [
			INTEGRATION,
			INTEGRATIONS,
			RELOAD_INTEGRATIONS,
			SHOW_MODAL_INTEGRATION
		]),
		pageTitle() {
			let title = 'Integrations';
			if (this.customerName) {
				title += ` on ${this.customerName}`;
			}
			return title;
		},
		hasResults() {
			return this[INTEGRATIONS].items?.length;
		},
		resultCounter() {
			if (!this[INTEGRATIONS].items) {
				return '0';
			}

			return `${this[INTEGRATIONS].items?.length} of ${this[INTEGRATIONS].totalCount}`;
		},
		hasMoreItemsToLoad() {
			return (
				this.integrationsList.length <
				_.get(this[INTEGRATIONS], 'totalCount', 0)
			);
		},
		integrationsList() {
			return _.get(this[INTEGRATIONS], 'items', []);
		},
		urlNames() {
			let name = _.get(this.thirdParty, 'dcsIntegration', '');
			return [
				{
					name: 'Third Party Manager',
					data: { path: '/integrations/thirdparties' }
				},
				{ name: name }
			];
		}
	},
	methods: {
		...mapActions(MODULE_NAME, [SET_INTEGRATIONS, 'getThirdPartyById']),
		addIntegration() {
			this.$store.dispatch(
				`${MODULE_NAME}/${SHOW_MODAL_INTEGRATION}`,
				true
			);
		},
		async fetchData(page) {
			let query = { ...this.filters };
			query.search = decodeURIComponent(query.search);

			if (!query.customerId) {
				delete query.customerId;
			}
			query.page = page;
			query.count = this.countPerPage || 100;
			if (query.status) {
				query.active =
					query.status === 'all'
						? null
						: query.status === 'active'
						? true
						: false;
			}
			delete query.status;
			if (!this.loadingMore) {
				this.loading = true;
			}

			if (_.isEmpty(this.thirdParty) && this.thirdPartyId) {
				await this.getThirdPartyById(this.thirdPartyId).then(res => {
					this.thirdParty = _.cloneDeep(res);
				});
			}

			query.typesId = this.thirdParty.id;
			query.partyId = this.thirdParty.partyId;
			if (!this.loadingMore) {
				this.loading = true;
			}
			await this[SET_INTEGRATIONS](query);

			this.loading = false;
			this.loadingMore = false;
		},
		updateRoute(queries) {
			let queryObj = getDifferentValues(this.defaultFilters, queries);
			if (this.countPerPage) {
				queryObj.count = this.countPerPage;
			}
			if (this[SHOW_MODAL_INTEGRATION]) {
				if (queries.integrationId) {
					queryObj.integrationId = this.integrationIdData;
				}
			} else {
				delete queryObj.integrationId;
			}

			this.$router
				.push({
					path: `/integrations/thirdparties/${this.thirdPartyId}/integrations`,
					query: queryObj
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			immediate: true,
			handler(newValue) {
				this.updateRoute(newValue);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		[SHOW_MODAL_INTEGRATION]: {
			handler(val) {
				if (!_.isEmpty(this[INTEGRATION])) {
					this.integrationIdData = this[INTEGRATION].id;
				} else {
					this.integrationIdData = 0;
				}
				if (val && this.integrationIdData > 0) {
					this.updateRoute({
						...this.filters,
						integrationId: this.integrationIdData
					});
				} else {
					this.updateRoute(this.filters);
				}
			}
		},
		'filters.customerId': {
			immediate: true,
			handler(val) {
				this.customerName = this[GET_CUSTOMER_OPTION_NAME](val);
			}
		},
		integrationId: {
			handler(val) {
				this.integrationIdData = parseInt(val);
			}
		},
		[RELOAD_INTEGRATIONS]: {
			handler(val) {
				if (val) {
					this.fetchData(1);
				}
			}
		},
		'$route.query': {
			handler(newQuery) {
				if (newQuery.integrationId && _.isEmpty(this[INTEGRATION])) {
					let integrationsObj = this[INTEGRATIONS].items.find(
						item => item.id === this.integrationIdData
					);
					this.$store.dispatch(
						`integrations/setIntegration`,
						integrationsObj
					);
				}
				if (!newQuery.integrationId) {
					let newfilters = { ...this.defaultFilters, ...newQuery };

					if (!_.isEqual(newfilters, this.filters)) {
						this.filters = _.cloneDeep(newfilters);
					}
					this.$store.dispatch(
						`integrations/showModalIntegration`,
						false
					);
				}
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.results-toolbar__filter {
	display: flex;
}
.results-toolbar__counter {
	margin-top: 8px;
}
</style>
